/**
 * OfficeEmpPreview Component
 * 
 * Main component for displaying and managing office employee payslip previews.
 * Handles both admin view (full access) and employee view (limited access).
 * Supports both salary (給与) and bonus (賞与) payslip types.
 * 
 * Features:
 * - Payslip data fetching and display
 * - Settings management (colors, fonts, field visibility) - separate for salary and bonus
 * - PDF generation
 * - Employee-specific data filtering
 * - Year, month (salary) / period (bonus) selection
 * - Responsive design support
 * - Data type switching (salary/bonus)
 * 
 * @component
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import '../../css/OfficeEmpPreview.css';
import { PayslipTable } from './PayslipTable';
import { SettingsModal } from './SettingsModal';
import { ControlsSection } from './ControlsSection';
import { loadSettings, saveSettings, PayslipSettings, getDefaultSettings } from './SettingsUtils';
import { handlePdfDownload } from './PdfDownloadUtils';
import { fileUploadService } from '../../../../services/FileUploadService';
import {
  paymentItems,
  deductionItems,
  attendanceItems,
  bonusPaymentItems,
  bonusDeductionItems,
  bonusAttendanceItems,
  salaryNotesItems,
  bonusNotesItems
} from './DataConfiguration';

/**
 * Type definitions for payslip data structures.
 * These types are defined locally since they don't exist in EmployeeService.
 */

/**
 * Payment data interface (for salary payslips).
 * @interface
 */
interface PaymentData {
  [key: string]: any;
}

interface DeductionData {
  [key: string]: any;
}

interface AttendanceData {
  [key: string]: any;
}

interface BonusPaymentData {
  [key: string]: any;
}

interface BonusDeductionData {
  [key: string]: any;
}

interface BonusAttendanceData {
  [key: string]: any;
}

interface SalaryNotesData {
  [key: string]: any;
}

interface BonusNotesData {
  [key: string]: any;
}

interface Employee {
  code: string;
  name: string;
  department: string;
}

/**
 * Props interface for OfficeEmpPreview component.
 */
interface OfficeEmpPreviewProps {
  isEmployeeView?: boolean;        // Whether this is employee view (limited access) or admin view
  currentEmployeeCode?: string;    // Employee code (4-digit) for employee view
  currentEmployeeName?: string;    // Employee name for employee view (used to fetch salary/bonus by name)
}

/**
 * OfficeEmpPreview Component
 * 
 * Main preview component for office employee payslips.
 * Supports both admin view (full functionality) and employee view (limited access).
 * Handles both salary and bonus payslip types with separate settings for each.
 * 
 * @param props - Component props
 * @returns React functional component
 */
export const OfficeEmpPreview: React.FC<OfficeEmpPreviewProps> = ({
  isEmployeeView = false,
  currentEmployeeCode = '',
  currentEmployeeName = ''
}) => {
  const isEmptyResult = (value: unknown): boolean => {
    if (value == null) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value as Record<string, unknown>).length === 0;
    return false;
  };

  /**
   * State management:
   * - dataType: Type of payslip data ('salary' or 'bonus')
   * - selectedYear/Month/Period: Selected year, month (for salary), or period (for bonus)
   * - empCode: Selected employee code (main state)
   * - tempEmpCode: Temporary employee code for input field (allows typing without immediate search)
   * - employeeName: Employee name for display
   * - warningMessage: Warning message to display to user
   * - hasSearched: Whether a search has been performed (for conditional rendering)
   * - paymentData/deductionData/attendanceData/salaryNotesData: Fetched salary payslip data
   * - bonusPaymentData/bonusDeductionData/bonusAttendanceData/bonusNotesData: Fetched bonus payslip data
   * - availableYears/Months/Periods: Available options for dropdowns
   * - bonusPeriodMapping: Mapping from month display names to bonus periods
   * - showSettings: Whether settings modal is open
   * - showTable: Whether to show payslip table (always true currently)
   * - salarySettings/bonusSettings: Separate payslip settings for salary and bonus
   */
  const [dataType, setDataType] = useState<'salary' | 'bonus'>('salary');
  const [selectedMonth, setSelectedMonth] = useState<string>('');        // Selected month (for salary)
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');      // Selected period (for bonus)
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [empCode, setEmpCode] = useState<string>('');                    // Main employee code state
  const [tempEmpCode, setTempEmpCode] = useState<string>('');           // Temporary state for input field
  const [employeeName, setEmployeeName] = useState<string>('');
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  
  // Salary payslip data
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [deductionData, setDeductionData] = useState<DeductionData | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceData | null>(null);
  const [salaryNotesData, setSalaryNotesData] = useState<SalaryNotesData | null>(null);
  
  // Bonus payslip data
  const [bonusPaymentData, setBonusPaymentData] = useState<BonusPaymentData | null>(null);
  const [bonusDeductionData, setBonusDeductionData] = useState<BonusDeductionData | null>(null);
  const [bonusAttendanceData, setBonusAttendanceData] = useState<BonusAttendanceData | null>(null);
  const [bonusNotesData, setBonusNotesData] = useState<BonusNotesData | null>(null);
  
  // Available options for dropdowns
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableMonths, setAvailableMonths] = useState<number[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [bonusPeriodMapping, setBonusPeriodMapping] = useState<{[month: string]: string}>({});
  
  // UI state
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showTable, setShowTable] = useState<boolean>(true);
  
  /**
   * Ref for table element (used for PDF generation).
   */
  const tableRef = useRef<HTMLDivElement>(null);

  /**
   * Separate payslip customization settings for salary and bonus.
   * 
   * Each payslip type (salary/bonus) has its own settings (colors, fonts, field visibility).
   * This allows users to customize each type independently.
   */
  const [salarySettings, setSalarySettings] = useState<PayslipSettings | null>(null);
  const [bonusSettings, setBonusSettings] = useState<PayslipSettings | null>(null);



  /**
   * Maps period values to Japanese display names.
   *
   * Converts bonus period values (English, numeric, Japanese abbreviations) to
   * standardized Japanese display names for the period dropdown.
   *
   * Supported formats:
   * - English: 'summer' -> '8月', 'first' -> '第1期', etc.
   * - Numeric: '1' -> '第1期', '2' -> '第2期', etc.
   * - Japanese abbreviations: '夏' -> '8月', '通常' -> '通常賞与', etc.
   * - Month numbers: '1' -> '1月', '12' -> '12月' (if between 1-12)
   *
   * @param period - Period value to convert (string)
   * @returns Japanese display name for the period
   *
   * @example
   * ```typescript
   * getPeriodDisplayName('first');     // Returns: '第1期'
   * getPeriodDisplayName('8');         // Returns: '8月'
   * getPeriodDisplayName('summer');    // Returns: '8月'
   * ```
   */
  const getPeriodDisplayName = (period: string): string => {
    const periodMappings: { [key: string]: string } = {
      // English to Japanese mappings - using months instead of seasons
      'summer': '8月',
      'winter': '12月',
      'spring': '3月',
      'autumn': '11月',
      'first': '第1期',
      'second': '第2期',
      'third': '第3期',
      'fourth': '第4期',
      'mid': '中間賞与',
      'year-end': '年末賞与',
      'bonus': '賞与',
      'special': '特別賞与',
      
      // Numeric to Japanese mappings
      '1': '第1期',
      '2': '第2期',
      '3': '第3期',
      '4': '第4期',
      '5': '第5期',
      '6': '第6期',
      
      // Japanese abbreviations to full Japanese - using months instead of seasons
      '夏': '8月',
      '冬': '12月',
      '春': '3月',
      '秋': '11月',
      
      // Common Japanese bonus terms
      '通常': '通常賞与',
      '特別': '特別賞与',
      '臨時': '臨時賞与',
      '決算': '決算賞与'
    };
    
    /**
     * If period is a number (month 1-12), format it as "X月".
     * For example: '1' -> '1月', '12' -> '12月'
     */
    const monthNum = parseInt(period);
    if (!isNaN(monthNum) && monthNum >= 1 && monthNum <= 12) {
      return `${monthNum}月`;
    }
    
    // Return Japanese name if mapping exists, otherwise return original value
    return periodMappings[period.toLowerCase()] || period;
  };

  /**
   * Load payslip settings on component mount.
   * 
   * Fetches saved settings from API (with localStorage fallback) for both salary and bonus.
   * Settings are loaded separately for each payslip type to allow independent customization.
   */
  useEffect(() => {
    const loadInitialSettings = async () => {
      try {
        const [salary, bonus] = await Promise.all([
          loadSettings('salary'),
          loadSettings('bonus')
        ]);
        setSalarySettings(salary);
        setBonusSettings(bonus);
      } catch (error) {
        console.error('Error loading payslip settings:', error);
      }
    };
    
    loadInitialSettings();
  }, []);

  /**
   * Get current settings based on dataType - memoized to prevent unnecessary recalculations.
   * 
   * Returns settings for the current payslip type (salary or bonus).
   * Always ensures valid settings are returned (never null/undefined) to prevent errors
   * on older smartphones that may not handle null values gracefully.
   * 
   * @returns PayslipSettings for current data type
   */
  const payslipSettings = useMemo(() => {
    const currentSettings = dataType === 'salary' ? salarySettings : bonusSettings;
    // Ensure settings are always valid to prevent errors on older smartphones
    return currentSettings || getDefaultSettings(dataType);
  }, [dataType, salarySettings, bonusSettings]);

  /**
   * Force refresh settings when dataType changes.
   * 
   * When switching between salary and bonus payslip types:
   * - Resets all selections to ensure clean state
   * - Clears data for the previous type
   * - Fetches available years/months or years/periods for the new type
   * 
   * This ensures users start with a fresh state when switching payslip types.
   */
  useEffect(() => {
    /**
     * Reset all selections when switching data types to ensure clean state.
     * Different fields are cleared depending on whether switching to salary or bonus.
     */
    if (dataType === 'salary') {
      setSelectedPeriod('');
      setSelectedYear('');
      setEmpCode('');
      setTempEmpCode('');
      setEmployeeName('');
      setWarningMessage('');
      setHasSearched(false);
      setPaymentData(null);
      setDeductionData(null);
      setAttendanceData(null);
      setSalaryNotesData(null);
      setTableData({ memo1: '', memo2: '' });
    } else {
      setSelectedMonth('');
      setSelectedYear('');
      setEmpCode('');
      setTempEmpCode('');
      setEmployeeName('');
      setWarningMessage('');
      setHasSearched(false);
      setBonusPaymentData(null);
      setBonusDeductionData(null);
      setBonusAttendanceData(null);
      setBonusNotesData(null);
      setTableData({ memo1: '', memo2: '' });
    }
    
    /**
     * Force fetch available data when switching data types.
     * 
     * Fetches available years and months/periods for the new payslip type.
     * For salary: fetches years and months
     * For bonus: fetches years and periods (extracts from bonus names)
     */
    const fetchDataForType = async () => {
      try {
        if (dataType === 'bonus') {
          const bonusData = await fileUploadService.fetchBonusDataWithFilters();
          
          if (bonusData && bonusData.length > 0) {
            const sampleItem = bonusData[0];
            
            const uniqueYears = Array.from(new Set(bonusData.map((item: any) => item.year))).sort((a: any, b: any) => (b as number) - (a as number)) as number[];
            setAvailableYears(uniqueYears);
            
            /**
             * Use the correct field name: bonusPeriod.
             * If bonusPeriod is not available, try to find it by searching for similar field names.
             */
            let periods: any[] = [];
            if (sampleItem.bonusPeriod !== undefined) {
              periods = bonusData.map((item: any) => item.bonusPeriod);
            } else {
              /**
               * Fallback: Look for any field that might contain period information.
               * Searches for fields with 'period', 'month', or 'term' in the name.
               */
              const allKeys = Object.keys(sampleItem);
              const possiblePeriodKeys = allKeys.filter(key => 
                key.toLowerCase().includes('period') || 
                key.toLowerCase().includes('month') ||
                key.toLowerCase().includes('term')
              );
              if (possiblePeriodKeys.length > 0) {
                periods = bonusData.map((item: any) => item[possiblePeriodKeys[0]]);
              }
            }
            
            const uniquePeriods = Array.from(new Set(periods)).sort((a: any, b: any) => (a as string).localeCompare(b as string)) as string[];
            setAvailablePeriods(uniquePeriods);
            setAvailableMonths([]);
          } else {
            setAvailableYears([]);
            setAvailablePeriods([]);
            setAvailableMonths([]);
          }
        } else {
          const salaryData = await fileUploadService.fetchSalaryDataWithFilters();
          const uniqueYears = Array.from(new Set(salaryData.map((item: any) => item.year))).sort((a: any, b: any) => (b as number) - (a as number)) as number[];
          setAvailableYears(uniqueYears);
          
          if (uniqueYears.length > 0) {
            const firstYearData = salaryData.filter((item: any) => item.year === uniqueYears[0]);
            const uniqueMonths = Array.from(new Set(firstYearData.map((item: any) => item.month))).sort((a: any, b: any) => (a as number) - (b as number)) as number[];
            setAvailableMonths(uniqueMonths);
          }
          setAvailablePeriods([]);
        }
      } catch (error) {
      }
    };
    
    fetchDataForType();
  }, [dataType]);

  /**
   * Separate effect to handle immediate memo update when toggling data types.
   * 
   * Updates memo data (個人メッセージ) when data type changes or when payment data changes.
   * Memo data comes from personalMessage1 and personalMessage2 fields in payment data.
   */
  useEffect(() => {
    if (dataType === 'salary' && paymentData) {
      setTableData({
        memo1: paymentData.personalMessage1 || '',
        memo2: paymentData.personalMessage2 || ''
      });
    } else if (dataType === 'bonus' && bonusPaymentData) {
      setTableData({
        memo1: bonusPaymentData.personalMessage1 || '',
        memo2: bonusPaymentData.personalMessage2 || ''
      });
    } else if (dataType === 'bonus' && !bonusPaymentData) {
      setTableData({
        memo1: '',
        memo2: ''
      });
    }
  }, [dataType, paymentData, bonusPaymentData]);

  /**
   * Fetch available years and months/periods when component mounts or dataType changes.
   * 
   * For salary: Fetches available years and months from salary data.
   * For bonus: Fetches available years and periods from bonus data (extracts from bonus names).
   */
  useEffect(() => {
    const fetchAvailableData = async () => {
      try {
        if (dataType === 'salary') {
          /**
           * Fetch available years and months for salary data.
           * Gets unique years from salary data and months for the first available year.
           */
          const salaryData = await fileUploadService.fetchSalaryDataWithFilters();
          const uniqueYears = Array.from(new Set(salaryData.map((item: any) => item.year))).sort((a: any, b: any) => (b as number) - (a as number)) as number[];
          setAvailableYears(uniqueYears);
          
          // Get months for the first available year (if any)
          if (uniqueYears.length > 0) {
            const firstYearData = salaryData.filter((item: any) => item.year === uniqueYears[0]);
            const uniqueMonths = Array.from(new Set(firstYearData.map((item: any) => item.month))).sort((a: any, b: any) => (a as number) - (b as number)) as number[];
            setAvailableMonths(uniqueMonths);
          }
          // Clear periods and mapping for salary mode (periods are only for bonus)
          setAvailablePeriods([]);
          setBonusPeriodMapping({});
        } else {
          /**
           * Fetch available years and periods for bonus data.
           * Extracts periods from bonus names (e.g., "R6年7月賞与" -> "7月")
           * and creates mapping from month display to bonus period.
           */
          const bonusData = await fileUploadService.fetchBonusDataWithFilters();
          
          if (bonusData && bonusData.length > 0) {
            // Check if the data has the expected structure
            const sampleItem = bonusData[0];
            
            const uniqueYears = Array.from(new Set(bonusData.map((item: any) => item.year))).sort((a: any, b: any) => (b as number) - (a as number)) as number[];
            setAvailableYears(uniqueYears);
            
            /**
             * Get bonus names for bonus data (extract months from bonus names).
             * Bonus names are in format like "R6年7月賞与" and we extract "7月" as display name.
             * Also creates mapping from month display (e.g., "7月") to bonus period value.
             */
            let bonusNames: any[] = [];
            const periodMapping: {[month: string]: string} = {};
            
            if (sampleItem.bonusName !== undefined) {
              /**
               * Extract months from bonus names like "R6年7月賞与" -> "7月".
               * Creates mapping from month display to bonus period for later use.
               */
              bonusNames = bonusData.map((item: any) => {
                const bonusName = item.bonusName;
                const bonusPeriod = item.bonusPeriod;
                if (!bonusName) return null;
                
                // Extract month from patterns like "R6年7月賞与" -> "7月"
                const monthMatch = bonusName.match(/(\d+)月/);
                const monthDisplay = monthMatch ? `${monthMatch[1]}月` : bonusName;
                
                // Create mapping from month display to bonus period (for API calls)
                if (bonusPeriod !== undefined && bonusPeriod !== null) {
                  periodMapping[monthDisplay] = bonusPeriod.toString();
                }
                
                return monthDisplay;
              }).filter((name: any) => name !== null);
            }
            
            /**
             * Sort bonus names by month number for proper ordering.
             * For example: "1月", "2月", ..., "12月" instead of alphabetical order.
             */
            const uniqueBonusNames = Array.from(new Set(bonusNames)).sort((a: any, b: any) => {
              // Sort by month number for proper ordering
              const monthA = parseInt(a.replace('月', ''));
              const monthB = parseInt(b.replace('月', ''));
              return monthA - monthB;
            }) as string[];
            setAvailablePeriods(uniqueBonusNames);
            setBonusPeriodMapping(periodMapping);
            
            // Clear months for bonus mode (months are only for salary)
            setAvailableMonths([]);
          } else {
            setAvailableYears([]);
            setAvailablePeriods([]);
            setAvailableMonths([]);
            setBonusPeriodMapping({});
          }
        }
      } catch (error) {
      }
    };
    
    fetchAvailableData();
  }, [dataType]);

  /**
   * Fetches employee data and payslip information by employee code.
   * 
   * Process:
   * 1. Validates employee code
   * 2. Fetches employee information from database
   * 3. If year and month/period are selected, fetches payslip data
   * 4. Updates memo data from payment data
   * 
   * Supports both salary and bonus payslip types.
   * 
   * @param code - Employee code to search for
   */
  const fetchEmployeeData = useCallback(async (code: string, nameForData?: string) => {
    if (!code.trim()) {
      setEmployeeName('');
      setWarningMessage('');
      setHasSearched(false);
      return;
    }
    const useNameForLookup = nameForData != null && nameForData.trim() !== '';

    try {
      // Fetch employee information from the database (by 4-digit code)
      const employee = await fileUploadService.getEmployeeByCode(code);
      if (employee) {
        setEmployeeName(employee.name);
        setWarningMessage(''); // Clear any previous warning
        setHasSearched(true); // Mark that we've searched and found an employee
        setEmpCode(code); // Only update main empCode state when employee is found

        // Fetch payment, deduction, and attendance data if year and month/period are selected
        if (selectedYear && (dataType === 'salary' ? selectedMonth : selectedPeriod)) {
          const year = parseInt(selectedYear);
          // Employee view: use nameForData (session). Admin view: use employee.name (from account looked up by 4-digit code).
          const lookupName = useNameForLookup ? nameForData!.trim() : (employee.name != null ? employee.name.trim() : undefined);

          try {
            /**
             * Fetch all data in parallel based on data type.
             * Salary/bonus data is fetched by name (employee view: session name; admin: account name from 4-digit code).
             */
            if (dataType === 'salary') {
              const month = parseInt(selectedMonth);

              /**
               * Fetch actual salary data from the database.
               * Fetches payment, deduction, attendance, and notes data in parallel.
               */
              const [payment, deduction, attendance, salaryNotes] = await Promise.all([
                fileUploadService.getPaymentData(code, year, month, lookupName),
                fileUploadService.getDeductionData(code, year, month, lookupName),
                fileUploadService.getAttendanceData(code, year, month, lookupName),
                fileUploadService.getSalaryNotesData(code, year, month, lookupName)
              ]);
              
              // Set salary data
              setPaymentData(payment);
              setDeductionData(deduction);
              setAttendanceData(attendance);
              setSalaryNotesData(salaryNotes);

              const noSalaryData =
                isEmptyResult(payment) &&
                isEmptyResult(deduction) &&
                isEmptyResult(attendance) &&
                isEmptyResult(salaryNotes);
              setWarningMessage(noSalaryData ? `指定された条件（年: ${year}, 月: ${month}）で給与データが見つかりません。` : '');
              
              /**
               * Update memo data from payment data.
               * Memo fields (個人メッセージ) are stored in payment data.
               */
              if (payment) {
                setTableData({
                  memo1: payment.personalMessage1 || '',
                  memo2: payment.personalMessage2 || ''
                });
              }
            } else {
              /**
               * Bonus data - convert selected period display name back to bonus period value.
               * Uses bonusPeriodMapping to convert month display (e.g., "7月") to period value.
               */
              const period = bonusPeriodMapping[selectedPeriod] || selectedPeriod;
              
              /**
               * Fetch actual bonus data from the database.
               * Fetches bonus payment, deduction, attendance, and notes data in parallel.
               */
              const [bonusPayment, bonusDeduction, bonusAttendance, bonusNotes] = await Promise.all([
                fileUploadService.getBonusPaymentData(code, year, period, lookupName),
                fileUploadService.getBonusDeductionData(code, year, period, lookupName),
                fileUploadService.getBonusAttendanceData(code, year, period, lookupName),
                fileUploadService.getBonusNotesData(code, year, period, lookupName)
              ]);
              
              // Set bonus data
              setBonusPaymentData(bonusPayment);
              setBonusDeductionData(bonusDeduction);
              setBonusAttendanceData(bonusAttendance);
              setBonusNotesData(bonusNotes);

              const noBonusData =
                isEmptyResult(bonusPayment) &&
                isEmptyResult(bonusDeduction) &&
                isEmptyResult(bonusAttendance) &&
                isEmptyResult(bonusNotes);
              setWarningMessage(noBonusData ? `指定された条件（年: ${year}, 期: ${selectedPeriod}）で賞与データが見つかりません。` : '');
              
              /**
               * Update memo data from bonus payment data.
               * Memo fields (個人メッセージ) are stored in bonus payment data.
               */
              if (bonusPayment) {
                setTableData({
                  memo1: bonusPayment.personalMessage1 || '',
                  memo2: bonusPayment.personalMessage2 || ''
                });
              }
            }
          } catch (error) {
            console.error('Salary data fetch error:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            setWarningMessage(`給与データの取得中にエラーが発生しました: ${errorMessage}`);
          }
        } else {
          setWarningMessage('年と月（または期）を選択してください。');
        }
      } else {
        setEmployeeName('');
        setPaymentData(null);
        setDeductionData(null);
        setAttendanceData(null);
        setBonusPaymentData(null);
        setBonusDeductionData(null);
        setBonusAttendanceData(null);
        setSalaryNotesData(null);
        setBonusNotesData(null);
        setWarningMessage(`従業員コード ${code} の従業員が見つかりません。正しいコードを入力してください。`);
        setHasSearched(true); // Mark that we've searched but found no employee
      }
    } catch (error) {
      console.error('Employee fetch error:', error);
      setEmployeeName('');
      const errorMessage = error instanceof Error ? error.message : String(error);
      setWarningMessage(`従業員情報の取得中にエラーが発生しました: ${errorMessage}`);
      setHasSearched(true); // Mark that we've searched but encountered an error
    }
  }, [selectedYear, selectedMonth, selectedPeriod, dataType, bonusPeriodMapping]);

  /**
   * Handle employee view - automatically set empCode and load data.
   * 
   * When in employee view, automatically sets the employee code from currentEmployeeCode prop
   * and fetches employee data. Employee code input is disabled in this mode.
   */
  useEffect(() => {
    if (isEmployeeView && currentEmployeeCode) {
      setEmpCode(currentEmployeeCode);
      setTempEmpCode(currentEmployeeCode);
      // Auto-load employee data for employee view (use name for salary/bonus lookup)
      fetchEmployeeData(currentEmployeeCode, currentEmployeeName || undefined);
    }
  }, [isEmployeeView, currentEmployeeCode, currentEmployeeName, fetchEmployeeData]);

  /**
   * Fetch filtered years/months/periods for employee view.
   * 
   * When in employee view, fetches only years and months/periods available for the current employee.
   * This provides a filtered view specific to the employee's payslip history.
   */
  useEffect(() => {
    if (isEmployeeView && currentEmployeeName) {
      const fetchEmployeeSpecificData = async () => {
        try {
          if (dataType === 'salary') {
            const years = await fileUploadService.getSalaryAvailableYearsByEmployeeName(currentEmployeeName);
            setAvailableYears(years);
            if (selectedYear) {
              const months = await fileUploadService.getSalaryAvailableMonthsByEmployeeNameAndYear(currentEmployeeName, parseInt(selectedYear));
              setAvailableMonths(months);
            }
          } else {
            const years = await fileUploadService.getBonusAvailableYearsByEmployeeName(currentEmployeeName);
            setAvailableYears(years);
            if (selectedYear) {
              const periods = await fileUploadService.getBonusAvailablePeriodsByEmployeeNameAndYear(currentEmployeeName, parseInt(selectedYear));
              setAvailablePeriods(periods);
            }
          }
        } catch (error) {
          console.error('Error fetching employee-specific data:', error);
        }
      };
      fetchEmployeeSpecificData();
    }
  }, [isEmployeeView, currentEmployeeName, dataType, selectedYear]);

  /**
   * Auto-refresh payslip when year, month, or period changes (after an employee is selected).
   * Avoids double-fetch on initial employee select by not depending on empCode.
   */
  useEffect(() => {
    const hasDateSelection = selectedYear && (dataType === 'salary' ? selectedMonth : selectedPeriod);
    if (empCode && hasDateSelection) {
      fetchEmployeeData(empCode, isEmployeeView && currentEmployeeName ? currentEmployeeName : undefined);
    }
  }, [selectedYear, selectedMonth, selectedPeriod, dataType, fetchEmployeeData, isEmployeeView, currentEmployeeName]);

  /**
   * Updates payslip settings and saves them to API.
   * 
   * Updates local state and persists settings to backend (with localStorage fallback).
   * Settings are saved separately for salary and bonus to allow independent customization.
   * 
   * @param newSettings - New payslip settings to apply
   */
  const updateSettings = async (newSettings: PayslipSettings) => {
    console.log(`[DEBUG] updateSettings called with:`, newSettings);
    console.log(`[DEBUG] dataType:`, dataType);
    
    if (dataType === 'salary') {
      setSalarySettings(newSettings);
      try {
        await saveSettings(newSettings, 'salary');
      } catch (error) {
        console.error('Error saving salary settings:', error);
      }
    } else {
      setBonusSettings(newSettings);
      try {
        await saveSettings(newSettings, 'bonus');
      } catch (error) {
        console.error('Error saving bonus settings:', error);
      }
    }
  };

  /**
   * Table data state for memo fields.
   * Stores personal messages (個人メッセージ) displayed on payslip.
   */
  const [tableData, setTableData] = useState({
    memo1: '',  // 個人メッセージ1
    memo2: ''   // 個人メッセージ2
  });

  /**
   * Handles year selection change.
   * 
   * When a year is selected:
   * - Fetches available months (for salary) or periods (for bonus) for that year
   * - Resets selected month/period
   * - In employee view, fetches employee-specific months/periods
   * 
   * @param year - Selected year string
   */
  const handleYearChange = async (year: string) => {
    setSelectedYear(year);
    setSelectedMonth(''); // Reset month when year changes
    setSelectedPeriod(''); // Reset period when year changes
    
    if (year) {
      try {
        if (dataType === 'salary') {
          /**
           * Fetch months for the selected year.
           * In employee view, fetches employee-specific months.
           * In admin view, fetches all available months for that year.
           */
          const yearNum = parseInt(year);
          
          // If in employee view, fetch employee-specific months (by name)
          if (isEmployeeView && currentEmployeeName) {
            const uniqueMonths = await fileUploadService.getSalaryAvailableMonthsByEmployeeNameAndYear(currentEmployeeName, yearNum);
            setAvailableMonths(uniqueMonths);
          } else {
            // Admin view: fetch all available months for that year
            const salaryData = await fileUploadService.fetchSalaryDataWithFilters(yearNum);
            const uniqueMonths = Array.from(new Set(salaryData.map((item: any) => item.month))).sort((a: any, b: any) => (a as number) - (b as number)) as number[];
            setAvailableMonths(uniqueMonths);
          }
        } else {
          /**
           * Fetch periods for the selected year (bonus).
           * In employee view, fetches employee-specific periods.
           * In admin view, fetches all available periods and extracts from bonus names.
           */
          const yearNum = parseInt(year);
          
          // If in employee view, fetch employee-specific periods (by name)
          if (isEmployeeView && currentEmployeeName) {
            const uniquePeriods = await fileUploadService.getBonusAvailablePeriodsByEmployeeNameAndYear(currentEmployeeName, yearNum);
            setAvailablePeriods(uniquePeriods);
          } else {
            /**
             * Admin view: Fetch all available periods for that year.
             * Extracts periods from bonus names and creates mapping.
             */
            const bonusData = await fileUploadService.fetchBonusDataWithFilters(yearNum);
            
            if (bonusData && bonusData.length > 0) {
              const sampleItem = bonusData[0];
            
              /**
               * Get bonus names for the selected year (extract months from bonus names).
               * Bonus names are in format like "R6年7月賞与" and we extract "7月" as display name.
               */
              let bonusNames: any[] = [];
              const periodMapping: {[month: string]: string} = {};
              
              if (sampleItem.bonusName !== undefined) {
                // Extract months from bonus names like "R6年7月賞与" -> "7月"
                bonusNames = bonusData.map((item: any) => {
                  const bonusName = item.bonusName;
                  const bonusPeriod = item.bonusPeriod;
                  if (!bonusName) return null;
                  
                  // Extract month from patterns like "R6年7月賞与" -> "7月"
                  const monthMatch = bonusName.match(/(\d+)月/);
                  const monthDisplay = monthMatch ? `${monthMatch[1]}月` : bonusName;
                  
                  // Create mapping from month display to bonus period (for API calls)
                  if (bonusPeriod !== undefined && bonusPeriod !== null) {
                    periodMapping[monthDisplay] = bonusPeriod.toString();
                  }
                  
                  return monthDisplay;
                }).filter((name: any) => name !== null);
              }
              
              /**
               * Sort bonus names by month number for proper ordering.
               * For example: "1月", "2月", ..., "12月" instead of alphabetical order.
               */
              const uniqueBonusNames = Array.from(new Set(bonusNames)).sort((a: any, b: any) => {
                // Sort by month number for proper ordering
                const monthA = parseInt(a.replace('月', ''));
                const monthB = parseInt(b.replace('月', ''));
                return monthA - monthB;
              }) as string[];
              setAvailablePeriods(uniqueBonusNames);
              setBonusPeriodMapping(periodMapping);
            } else {
              setAvailablePeriods([]);
              setBonusPeriodMapping({});
            }
          }
        }
      } catch (error) {
        if (dataType === 'salary') {
          setAvailableMonths([]);
        } else {
          setAvailablePeriods([]);
        }
      }
    }
  };

  /** Pick the latest bonus period label from employee-specific available periods. */
  const pickLatestBonusPeriod = (periods: string[]): string => {
    if (periods.length === 0) return '';
    const monthLikes = periods
      .map((p) => ({ p, m: Number.parseInt(p.replace('月', ''), 10) }))
      .filter((x) => !Number.isNaN(x.m) && x.m >= 1 && x.m <= 12);
    if (monthLikes.length > 0) {
      return monthLikes.reduce((a, b) => (a.m >= b.m ? a : b)).p;
    }
    return periods[periods.length - 1] ?? '';
  };

  /**
   * Default year/month on first open.
   *
   * - Employee / 自分の給与明細 ({@code isEmployeeView}): latest year and latest month (or period) with data
   * - Admin preview: previous calendar month (fallback to latest month in year if missing)
   */
  useEffect(() => {
    if (isEmployeeView) {
      if (!selectedYear && availableYears.length > 0) {
        void handleYearChange(String(Math.max(...availableYears)));
        return;
      }
      if (dataType === 'salary' && selectedYear && !selectedMonth && availableMonths.length > 0) {
        setSelectedMonth(String(Math.max(...availableMonths)));
        return;
      }
      if (dataType === 'bonus' && selectedYear && !selectedPeriod && availablePeriods.length > 0) {
        setSelectedPeriod(pickLatestBonusPeriod(availablePeriods));
      }
      return;
    }

    const now = new Date();
    const nowYear = now.getFullYear();
    const defaultPeriod = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const defaultYear = defaultPeriod.getFullYear();
    const defaultMonth = defaultPeriod.getMonth() + 1;

    const pickYear = (): string => {
      if (availableYears.length === 0) return String(defaultYear);
      if (availableYears.includes(defaultYear)) return String(defaultYear);
      if (availableYears.includes(nowYear)) return String(nowYear);
      return String(Math.max(...availableYears));
    };

    if (!selectedYear) {
      void handleYearChange(pickYear());
      return;
    }

    if (dataType === 'salary' && !selectedMonth && availableMonths.length > 0) {
      const monthToUse = availableMonths.includes(defaultMonth)
        ? defaultMonth
        : Math.max(...availableMonths);
      setSelectedMonth(String(monthToUse));
      return;
    }

    if (dataType === 'bonus' && !selectedPeriod && availablePeriods.length > 0) {
      const want = `${defaultMonth}月`;
      if (availablePeriods.includes(want)) {
        setSelectedPeriod(want);
        return;
      }
      setSelectedPeriod(pickLatestBonusPeriod(availablePeriods));
    }
  }, [
    isEmployeeView,
    dataType,
    availableYears,
    availableMonths,
    availablePeriods,
    selectedYear,
    selectedMonth,
    selectedPeriod,
  ]);

  /**
   * Ensure periods are loaded when switching to bonus mode.
   * 
   * If periods are not available when switching to bonus mode, fetches them.
   * This is a fallback to ensure periods are always available in bonus mode.
   */
  useEffect(() => {
    if (dataType === 'bonus' && availablePeriods.length === 0) {
      const loadBonusPeriods = async () => {
        try {
          const bonusData = await fileUploadService.fetchBonusDataWithFilters();
          const uniquePeriods = Array.from(new Set(bonusData.map((item: any) => item.bonusPeriod))).sort((a: any, b: any) => (a as string).localeCompare(b as string)) as string[];
          setAvailablePeriods(uniquePeriods);
        } catch (error) {
          // Silent fail - periods may not be available yet
        }
      };
      loadBonusPeriods();
    }
  }, [dataType, availablePeriods.length]);

  /**
   * Clears all form inputs and resets component state.
   * 
   * Used by the "Clear" button to reset the entire form.
   * Clears all selections, data, and warning messages.
   */
  const handleClear = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedPeriod('');
    setEmpCode('');
    setTempEmpCode(''); // Clear temporary employee code
    setEmployeeName(''); // Clear employee name when clearing form
    setWarningMessage(''); // Clear warning message when clearing form
    setHasSearched(false); // Reset search state when clearing form
    // Keep table visible - removed setShowTable(false)
    setPaymentData(null); // Clear payment data
    setDeductionData(null); // Clear deduction data
    setAttendanceData(null); // Clear attendance data
    setBonusPaymentData(null); // Clear bonus payment data
    setBonusDeductionData(null); // Clear bonus deduction data
    setBonusAttendanceData(null); // Clear bonus attendance data
    setSalaryNotesData(null); // Clear salary notes data
    setBonusNotesData(null); // Clear bonus notes data
    setTableData({ memo1: '', memo2: '' }); // Clear memo data (個人メッセージ)
  };

  /**
   * Handles PDF download button click.
   * 
   * Generates a PDF file from the payslip table using the current settings.
   * Uses the appropriate data type (salary/bonus) for filename and data selection.
   */
  const handlePdfDownloadClick = () => {
    console.log('PDF Download - Values:', {
      selectedYear,
      selectedMonth,
      selectedPeriod,
      empCode,
      dataType,
      hasSearched
    });
    handlePdfDownload(tableRef, selectedYear, selectedMonth, selectedPeriod, empCode, dataType, payslipSettings || getDefaultSettings(dataType));
  };


  // Fetch employee data when employee code changes

  return (
    <div className="office-emp-preview">
      {/* Apply CSS custom properties for payslip styling */}
      <style>
        {`
          .office-emp-preview {
            --payslip-border-color: ${(payslipSettings || getDefaultSettings(dataType)).borderColor};
            --payslip-background-color: ${(payslipSettings || getDefaultSettings(dataType)).backgroundColor};
          }
        `}
      </style>
      
      {/* Settings modal - only shown in admin view */}
      {!isEmployeeView && (
        <SettingsModal 
          isOpen={showSettings} 
          onClose={() => setShowSettings(false)}
          settings={payslipSettings || getDefaultSettings(dataType)}
          onSettingsChange={updateSettings}
          paymentItems={dataType === 'salary' ? paymentItems : bonusPaymentItems}
          deductionItems={dataType === 'salary' ? deductionItems : bonusDeductionItems}
          attendanceItems={dataType === 'salary' ? attendanceItems : bonusAttendanceItems}
          notesItems={dataType === 'salary' ? salaryNotesItems : bonusNotesItems}
          dataType={dataType}
        />
      )}
      
      {/* Control panel with filters and action buttons */}
      <ControlsSection
        dataType={dataType}
        selectedYear={selectedYear}
        selectedMonth={selectedMonth}
        selectedPeriod={selectedPeriod}
        empCode={tempEmpCode} // Use temporary code for input field
        showTable={showTable}
        availableYears={availableYears}
        availableMonths={availableMonths}
        availablePeriods={availablePeriods}
        onDataTypeChange={setDataType}
        onYearChange={handleYearChange}
        onMonthChange={(month) => {
          setSelectedMonth(month);
        }}
        onPeriodChange={(period) => {
          setSelectedPeriod(period);
        }}
        onEmpCodeChange={setTempEmpCode} // Update temporary code, not main code
        onEmpCodeSelect={async (empCode, empName) => {
          setTempEmpCode(empCode);
          await fetchEmployeeData(empCode);
        }}
        onClear={handleClear}
        onPdfDownload={handlePdfDownloadClick}
        onSettingsClick={() => setShowSettings(!showSettings)}
        getPeriodDisplayName={getPeriodDisplayName}
        isEmployeeView={isEmployeeView}
      />

      {/* Warning Message - displays validation errors or search status */}
      {warningMessage && (
        <div className="warning-message" style={{
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          padding: '12px',
          margin: '10px 0',
          color: '#856404',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          ⚠️ {warningMessage}
        </div>
      )}

      {/* Payslip Table - displays the actual payslip with all sections */}
      {showTable && (
        <div className="payslip-scale-wrapper">
          <div className="payslip-scale-inner" ref={tableRef}>
            <PayslipTable
              dataType={dataType}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              selectedPeriod={selectedPeriod}
              empCode={empCode}
              employeeName={employeeName}
              hasSearched={hasSearched}
              paymentData={paymentData}
              deductionData={deductionData}
              attendanceData={attendanceData}
              bonusPaymentData={bonusPaymentData}
              bonusDeductionData={bonusDeductionData}
              bonusAttendanceData={bonusAttendanceData}
              salaryNotesData={salaryNotesData}
              bonusNotesData={bonusNotesData}
              payslipSettings={payslipSettings || getDefaultSettings(dataType)}
              tableData={tableData}
              paymentItems={dataType === 'salary' ? paymentItems : bonusPaymentItems}
              deductionItems={dataType === 'salary' ? deductionItems : bonusDeductionItems}
              attendanceItems={dataType === 'salary' ? attendanceItems : bonusAttendanceItems}
              notesItems={dataType === 'salary' ? salaryNotesItems : bonusNotesItems}
              getPeriodDisplayName={getPeriodDisplayName}
            />
          </div>
        </div>
      )}
    </div>
  );
}; 